/* eslint-disable no-await-in-loop */

/* eslint-disable no-return-await */

/* eslint-disable class-methods-use-this */
import { DEFAULT_API_HANDLER_CONFIG } from "./constants";
import { extractRelevantHeaders, generateHash } from "./util";

export class RequestDeduplicator {
  constructor(config = {}) {
    this.config = { ...DEFAULT_API_HANDLER_CONFIG, ...config };
    this.pendingRequests = new Map();
    this.requestCache = new Map();
    this.isCleaningUp = false;
  }

  generateRequestKey(request) {
    const baseKey = `${(request.method || "GET").toUpperCase()}:${
      request.url
    }:${generateHash(request.params)}`;

    if ((request.method || "GET").toLowerCase() === "get") {
      return baseKey;
    }

    const method = request.method?.toLowerCase();

    if (["post", "put", "patch"].includes(method)) {
      const dataHash = request.data ? generateHash(request.data) : "";
      const relevantHeaders = extractRelevantHeaders(request.headers);
      const headersHash = relevantHeaders ? generateHash(relevantHeaders) : "";

      // Check for explicit deduplication flag
      if (request.allowDeduplication === true) {
        return `${baseKey}:${dataHash}:${headersHash}`;
      }

      // Default: Don't deduplicate (add unique identifier)
      const uniqueId = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      return `${baseKey}:${dataHash}:${headersHash}:${uniqueId}`;
    }

    // For DELETE and other methods, include data and relevant headers
    const dataHash = request.data ? generateHash(request.data) : "";
    const relevantHeaders = extractRelevantHeaders(request.headers);
    const headersHash = relevantHeaders ? generateHash(relevantHeaders) : "";

    return `${baseKey}:${dataHash}:${headersHash}`;
  }

  cleanupCache() {
    if (this.isCleaningUp) return;
    this.isCleaningUp = true;

    try {
      const now = Date.now();
      const cacheEntries = Array.from(this.requestCache.entries());

      // Remove expired entries
      const expiredKeys = cacheEntries
        .filter(([, { timestamp }]) => now - timestamp > this.config.cacheTTL)
        .map(([key]) => key);

      expiredKeys.forEach((key) => this.requestCache.delete(key));

      // Limit cache size
      if (this.requestCache.size > this.config.maxCacheSize) {
        const sortedEntries = cacheEntries.sort(
          (a, b) => a[1].timestamp - b[1].timestamp
        );

        const toDeleteCount = sortedEntries.length - this.config.maxCacheSize;
        const keysToDelete = sortedEntries
          .slice(0, toDeleteCount)
          .map(([key]) => key);

        keysToDelete.forEach((key) => this.requestCache.delete(key));
      }
    } finally {
      this.isCleaningUp = false;
    }
  }

  async deduplicateRequest(request, executor, options = {}) {
    const {
      enableCaching = this.config.enableCaching,
      bypassDeduplication = false,
    } = options;

    if (bypassDeduplication) {
      return executor();
    }

    const requestKey = this.generateRequestKey(request);

    // Check cache first (only for GET requests AND if caching is enabled)
    if (enableCaching && (request.method || "GET").toLowerCase() === "get") {
      this.cleanupCache();

      const cached = this.requestCache.get(requestKey);

      if (cached && Date.now() - cached.timestamp < this.config.cacheTTL) {
        return cached.data;
      }
    }

    // Check if request is already pending
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }

    let requestPromise;

    try {
      requestPromise = executor()
        .then((result) => {
          // Cache successful GET requests (only if caching is enabled)
          if (
            enableCaching &&
            (request.method || "GET").toLowerCase() === "get"
          ) {
            this.requestCache.set(requestKey, {
              data: result,
              timestamp: Date.now(),
            });
          }
          return result;
        })
        .finally(() => {
          // Always clean up pending request
          this.pendingRequests.delete(requestKey);
        });
    } catch (error) {
      // Handle synchronous errors
      this.pendingRequests.delete(requestKey);
      throw error;
    }

    this.pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  invalidateCache(pattern) {
    if (!pattern) {
      this.requestCache.clear();
      return;
    }

    const cacheKeys = Array.from(this.requestCache.keys());
    const keysToDelete = cacheKeys.filter((key) => key.includes(pattern));

    keysToDelete.forEach((key) => this.requestCache.delete(key));
  }

  cleanup() {
    this.pendingRequests.clear();
    this.requestCache.clear();
  }
}

export default RequestDeduplicator;
